import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  formatAsCurrency,
  Pagination,
  PartnerType,
  Skeleton,
  SubPartnerAccount,
  SubPartnerAccountStatus,
  isSubPartnerAccountStatus,
  useDateLocale,
  usePagination,
  useUpdateQueryParams,
  withStyles,
} from 'vopay';
import { BodyText, Button, TableFiltersContainer, Table, Title } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useCountryLabels } from '@/hooks';
import useFileDownload from '@/hooks/useFiledDownload';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import useFetchPartnerPartnersStatusSummary from '@/hooks/useApi/useFetchPartnerPartnersStatusSummary';
import useExportPartnerPartners from '@/hooks/useApi/useExportPartnerPartners';
import { formatApiDate } from '@/components/Entities/Accounts/accountStatusDate';
import AccountStatus from '@/components/Entities/Accounts/AccountStatus/AccountStatus';
import PartnerCreationDrawer from './PartnerCreationDrawer';
import PartnerDetailsDrawer from './PartnerDetailsDrawer/PartnerDetailsDrawer';
import PartnerManagementSearchForm from './PartnerManagementSearchForm';
import PartnersOverview from './PartnersOverview/PartnersOverview';
import { getPartnerStatusTiles } from './PartnersOverview/partnerOnboardingPipeline';
import * as partnerPageRoute from './partnerPage.route';
import styles from './PartnerManagementPage.module.scss';

const cn = withStyles(styles);

const PAGE_SIZE = 20;
const CSV_FILE_NAME = 'partners';
/** The partner types that may create partner accounts; the create-partner route enforces the same list. */
const PARTNER_CREATOR_TYPES: PartnerType[] = ['Technology', 'VAR', 'Referral'];

type QueryParams = {
  /** Empty for no status filter. */
  status: SubPartnerAccountStatus | '';
  searchText: string;
};

// Standalone (un-navigated) /partner-management route: the Partner Accounts page — its header and
// summary cards (ID-13789), partner creation in a drawer (ID-13334) and the spec 5.3 / 5.4 table,
// filters and CSV export (ID-13790). The /partners list and /partners-create page stay as they are.
export default function PartnerManagementPage() {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement' });
  const dateLocale = useDateLocale();
  const { fetching, partners, paginationMetaData, fetchPartnerPartners } = useFetchPartnerPartners();

  const { fetchingStatusSummary, statusSummary, fetchPartnerPartnersStatusSummary } =
    useFetchPartnerPartnersStatusSummary();

  const { exportingPartners, exportPartnerPartners } = useExportPartnerPartners();
  const { resolveCountryLabel } = useCountryLabels();
  const { downloadCsvFile } = useFileDownload();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<SubPartnerAccount | null>(null);
  // The hook's `fetchingStatusSummary` is false until the mount effect below runs; without this the
  // summary card would flash zeroed totals for one frame first.
  const [hasLoadedStatusSummary, setHasLoadedStatusSummary] = useState(false);

  const {
    response: { isRoleViewOnly, partnerType, partnerTypeLabel },
  } = usePartnerData();

  const { permissions } = usePermissions();

  // Technology, VAR and Referral partners onboard partners of their own; Inside Sales does not.
  const canAddPartner = partnerType !== '' && PARTNER_CREATOR_TYPES.includes(partnerType);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // An unknown status in the URL is ignored rather than sent.
  const status = useMemo(() => {
    const raw = queryParams.get('status');

    return isSubPartnerAccountStatus(raw) ? raw : '';
  }, [queryParams]);

  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  const filtersApplied = !!status || !!searchText;

  // One list for the summary card's tiles and the Status filter's options, so they always match: the
  // pipeline for this partner's type (its partner accounts share it), other populated statuses, then
  // the terminal ones. The status the URL filters on is in it too, even when nothing else would put it
  // there, so the applied filter is always visible.
  const statusTiles = useMemo(
    () =>
      getPartnerStatusTiles(partnerTypeLabel, permissions.partnerLiability, statusSummary?.StatusCounts ?? {}, status),
    [partnerTypeLabel, permissions.partnerLiability, statusSummary, status],
  );

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(paginationMetaData?.TotalRecords || 0, {
    pageSize: PAGE_SIZE,
  });

  // The filters the listing and its CSV export share.
  const filterParams = useMemo(
    () => ({ AccountStatus: status || undefined, SearchText: searchText || undefined }),
    [status, searchText],
  );

  const loadPartners = useCallback(async () => {
    try {
      await fetchPartnerPartners({ ...filterParams, Offset: offset, Limit: PAGE_SIZE });
    } catch (error) {
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Unable to Load Partners' }),
        error instanceof Error ? error.message : '',
      );
    }
    // alertDialog is deliberately left out: the provider recreates its value every render, so
    // depending on it would re-fire the fetch effect on every alert.
  }, [filterParams, offset, fetchPartnerPartners, t]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  // The summary behind the overview card and the Status filter: loaded on mount, and again whenever
  // the partner accounts it counts change (a partner created in the drawer).
  const loadStatusSummary = useCallback(async () => {
    try {
      await fetchPartnerPartnersStatusSummary();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('statusSummaryError', {
          defaultValue: 'Could not load the partner status summary: {{errorMessage}}',
          errorMessage,
        }),
      );
    } finally {
      setHasLoadedStatusSummary(true);
    }
    // toast is deliberately left out for the same reason as alertDialog above.
  }, [fetchPartnerPartnersStatusSummary, t]);

  useEffect(() => {
    loadStatusSummary();
  }, [loadStatusSummary]);

  async function handleExportCSV() {
    toast.neutral(
      t('exportCSVToast', {
        defaultValue: 'Your CSV export is being prepared. Large partner lists may take a few minutes to generate.',
      }),
    );

    try {
      downloadCsvFile(await exportPartnerPartners(filterParams), CSV_FILE_NAME);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('exportCSVError', { defaultValue: 'Could not export the partner list: {{errorMessage}}', errorMessage }),
      );
    }
  }

  // One line per currency: a partner account's accounts can hold CAD and USD and there is no
  // conversion between them (ID-13788 open question 3). A dash when it has no accounts yet.
  function renderTotalBalance({ data }: { data: SubPartnerAccount }) {
    const balances = Object.entries(data.TotalBalances);

    if (balances.length === 0) return <span className={cn('noBalance')}>-</span>;

    return (
      <div className={cn('balanceCell')}>
        {balances.map(([currency, amount]) => (
          <span key={currency}>{`${currency} ${formatAsCurrency(amount, '')}`}</span>
        ))}
      </div>
    );
  }

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        {/* Figma: H1/Large Title 32/Emphasized + Body/16/Regular (Text/Secondary75) */}
        <div className={cn('pageHeading')}>
          <Title>{t('title', { defaultValue: 'Partner Accounts' })}</Title>

          <BodyText className={cn('pageDescription')}>
            {t('pageDescription', {
              defaultValue: 'All partner accounts you have created, and where each one is in onboarding.',
            })}
          </BodyText>
        </div>

        {/* Figma `Buttons`: Size="Large"; Export CSV Type="Secondary", Add Partner Type="Primary" + Left Indicator="add" */}
        <div className={cn('headerActions')}>
          <Button
            id="partner-management-exportCSV"
            size="large"
            disabled={isRoleViewOnly || exportingPartners}
            onClick={handleExportCSV}
          >
            {t('exportCSVButton', { defaultValue: 'Export CSV' })}
          </Button>

          {canAddPartner && (
            <Button
              id="partners-addPartner"
              colorScheme="primary"
              size="large"
              iconLeft="add"
              disabled={isRoleViewOnly}
              onClick={() => setIsCreateDrawerOpen(true)}
            >
              {t('addPartnerButton', { defaultValue: 'Add Partner' })}
            </Button>
          )}
        </div>
      </Box>

      <PartnersOverview
        fetchingStatusSummary={fetchingStatusSummary || !hasLoadedStatusSummary}
        numTotalPartnerAccounts={statusSummary?.TotalPartnerAccounts ?? 0}
        statusCounts={statusSummary?.StatusCounts ?? {}}
        stages={statusTiles}
        // A tile is the Status filter set to that status, and clicking the selected one again clears it
        // (the Accounts page's ID-13981 rule); the keyword stays as it is.
        onStatusClick={(clickedStatus) => {
          setPageIndex(0);
          updateQueryParams({ status: clickedStatus === status ? '' : clickedStatus, searchText });
        }}
        selectedStatus={status}
      />

      <Card style={{ padding: '16px' }}>
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <PartnerManagementSearchForm
              status={status}
              searchText={searchText}
              statuses={statusTiles}
              isLoadingStatuses={fetchingStatusSummary}
              onSearch={(values) => {
                setPageIndex(0);

                updateQueryParams({
                  status: values.status,
                  searchText: values.searchText,
                });
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Box className={cn('tableContainer')}>
          {fetching ? (
            <Skeleton height="320px" containerClassName={cn('tableSkeleton')} data-testid="partnersTableSkeleton" />
          ) : partners.length === 0 ? (
            filtersApplied ? (
              <BodyText className={cn('noMatches')}>
                {t('noMatches', { defaultValue: 'No partners match the applied filters.' })}
              </BodyText>
            ) : (
              <div className={cn('emptyState')}>
                <Box flx="col" ai="center" gapSize="10px">
                  <Title level="4">{t('EmptyState.title', { defaultValue: 'No partners yet' })}</Title>

                  <p className={cn('emptyDescription')}>
                    {t('EmptyState.description', { defaultValue: 'Partners you create will appear here.' })}
                  </p>
                </Box>
              </div>
            )
          ) : (
            <Table
              id="partnersList"
              zebra
              rows={partners.map((partner) => ({ id: partner.PartnerAccountID, data: partner }))}
              columns={[
                {
                  id: 'dateAdded',
                  header: t('Table.dateAdded', { defaultValue: 'Date Added' }),
                  align: 'left',
                  template: ({ data }) => (
                    <span className={cn('dateCell')}>{formatApiDate(data.DateAdded, dateLocale)}</span>
                  ),
                },
                // Partner Name opens the details drawer (spec 5.3).
                {
                  id: 'partnerName',
                  header: t('Table.partnerName', { defaultValue: 'Partner Name' }),
                  align: 'left',
                  template: ({ data }) => (
                    <Table.LinkButton onClick={() => setSelectedPartner(data)}>{data.Name}</Table.LinkButton>
                  ),
                },
                {
                  id: 'status',
                  header: t('Table.status', { defaultValue: 'Status' }),
                  align: 'left',
                  // The shared badge (spec 5.3): activation date, terminal-status date or days in stage.
                  template: ({ data }) => <AccountStatus account={data} activeDateCopy="activatedOn" />,
                },
                {
                  id: 'country',
                  header: t('Table.country', { defaultValue: 'Country' }),
                  align: 'left',
                  template: ({ data }) => (data.Country ? resolveCountryLabel(data.Country) : ''),
                },
                {
                  id: 'accounts',
                  header: t('Table.accounts', { defaultValue: 'Accounts' }),
                  align: 'left',
                  template: ({ data }) => data.AccountCount,
                },
                {
                  id: 'totalBalance',
                  header: t('Table.totalBalance', { defaultValue: 'Total Balance' }),
                  align: 'right',
                  template: renderTotalBalance,
                },
              ]}
            />
          )}
        </Box>

        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => setPageIndex(e.selected)}
            />
          )}
        </Box>
      </Card>

      {isCreateDrawerOpen && (
        <PartnerCreationDrawer
          onClose={() => setIsCreateDrawerOpen(false)}
          // A new partner account is a new row and a new count, so both reads go again.
          onCreated={() => {
            loadPartners();
            loadStatusSummary();
          }}
        />
      )}

      <PartnerDetailsDrawer
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onViewFullDetails={(partner) =>
          history.push(partnerPageRoute.getPath({ partnerAccountID: partner.PartnerAccountID }))
        }
      />
    </Box>
  );
}
